/**
 * frontend/src/modules/marche/lib/api.ts
 * Helper pour appeler les endpoints KPI Marché.
 *
 * Backend réel : GET /api/v1/market/kpis/{slug} + query params (CommonKpiQuery).
 * Note : spec_openapi V1 indique POST, mais l'implémentation Phase 4.C.2 utilise GET.
 *
 * Jalon J1 — Phase 4.I.3 · spec_kpis.md §19 · DECISIONS #078
 */

import { apiClient } from '@/lib/apiClient';
import type { KpiApiResponse, MarcheQueryParams } from '../types/marche.types';

/**
 * Converts MarcheQueryParams to URLSearchParams, omitting undefined/null values.
 * Boolean `include_south` is serialized as 'true'/'false' (backend boolParam schema).
 */
function toSearchParams(params: MarcheQueryParams): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'boolean') {
      sp.set(k, v ? 'true' : 'false');
    } else {
      sp.set(k, String(v));
    }
  }
  return sp;
}

/**
 * Fetch a KPI from GET /api/v1/market/kpis/{slug}.
 *
 * @param slug — KPI slug (e.g. 'pdm-volume', 'numeric-distribution')
 * @param params — optional filter query params
 */
export async function fetchKpi<T>(
  slug: string,
  params?: MarcheQueryParams,
): Promise<KpiApiResponse<T>> {
  const qs = params ? `?${toSearchParams(params).toString()}` : '';
  const res = await apiClient.get<KpiApiResponse<T>>(`/market/kpis/${slug}${qs}`);
  return res.data;
}

/**
 * Fetch a wholesale KPI from GET /api/v1/market/wholesale/{path}.
 * H-23 patch (Phase 4.C-ter) — changed from POST to GET + query string.
 *
 * @param path — e.g. 'by-cluster'
 * @param params — filter params (channelType forced to 'wholesale' by backend)
 */
export async function fetchWholesaleKpi<T>(
  path: string,
  params?: MarcheQueryParams,
): Promise<KpiApiResponse<T>> {
  const qs = params ? `?${toSearchParams(params).toString()}` : '';
  const res = await apiClient.get<KpiApiResponse<T>>(`/market/wholesale/${path}${qs}`);
  return res.data;
}

/**
 * Fetch wholesale cluster detail from GET /api/v1/market/wholesale/cluster/{id}.
 *
 * @param clusterId — UUID of the market cluster
 * @param params — optional period filter
 */
export async function fetchWholesaleCluster<T>(
  clusterId: string,
  params?: Pick<MarcheQueryParams, 'period_preset' | 'date_from' | 'date_to'>,
): Promise<KpiApiResponse<T>> {
  const qs = params ? `?${toSearchParams(params).toString()}` : '';
  const res = await apiClient.get<KpiApiResponse<T>>(`/market/wholesale/cluster/${clusterId}${qs}`);
  return res.data;
}

/**
 * Fetch compare channels from GET /api/v1/market/compare-channels.
 * H-23 patch (Phase 4.C-ter) — changed from POST to GET + query string.
 */
export async function fetchCompareChannels<T>(
  params?: MarcheQueryParams,
): Promise<KpiApiResponse<T>> {
  const qs = params ? `?${toSearchParams(params).toString()}` : '';
  const res = await apiClient.get<KpiApiResponse<T>>(`/market/compare-channels${qs}`);
  return res.data;
}
